import { getSupabaseClient } from '../../lib/supabase';
import {
  buildLearningSessionSummaries,
  type HistoryAnswerRow,
  type HistoryAssignmentItemRow,
  type HistoryAssignmentRow,
  type HistoryAttemptRow,
  type HistorySessionRow,
} from '../history/sessionHistory';
import { buildLearnerInsight, classifyMasteryState } from './learnerInsightAdapter';
import {
  assertParentProgressScope,
  assertTutorSubjectScope,
  isTutorAssignmentActive,
  type ParentScope,
  type TutorScope,
} from './learnerInsightScope';
import type {
  AuthoritativeMasteryState,
  LearnerInsight,
  LearnerInsightInput,
  LearnerNoteInsight,
  LearnerNoteVisibility,
  LearnerSkillProgressInput,
} from './learnerInsightTypes';

export type TutorLearnerScopeOption = TutorScope & {
  studentCode: string;
  studentName: string;
  subjectCode: string;
  subjectName: string;
};

export type TutorRosterSubject = {
  subjectId: string;
  subjectCode: string;
  subjectName: string;
  attentionCount: number;
  learningCount: number;
  strongCount: number;
  lastActivityAt: string | null;
};

export type TutorRosterStudent = {
  studentId: string;
  studentCode: string;
  studentName: string;
  subjects: TutorRosterSubject[];
  attentionCount: number;
  learningCount: number;
  strongCount: number;
  lastActivityAt: string | null;
};

export type TutorRosterBundle = {
  scopes: TutorLearnerScopeOption[];
  students: TutorRosterStudent[];
};

type RosterSkillRow = {
  id: string;
  subject_id: string;
};

type RosterProgressRow = {
  student_id: string;
  skill_id: string;
  mastery_state: AuthoritativeMasteryState;
  last_activity_at: string | null;
};

export type ParentLearnerScopeOption = ParentScope & {
  studentCode: string;
  studentName: string;
  relationshipLabel: string;
  isPrimary: boolean;
};

export type ParentLearnerSubjectOption = {
  subjectId: string;
  subjectCode: string;
  subjectName: string;
  lastActivityAt: string | null;
};

type StudentRow = {
  id: string;
  student_code: string;
  display_name: string;
};

type SubjectRow = {
  id: string;
  code: string;
  name: string;
};

type SkillRow = {
  id: string;
  code: string;
  learner_statement: string | null;
  canonical_statement: string;
};

type SkillProgressRow = {
  skill_id: string;
  mastery_state: AuthoritativeMasteryState;
  mastery_percent: number;
  evidence_count: number;
  last_activity_at: string | null;
  average_active_seconds: number | null;
  average_hint_count: number | null;
};

function localIsoDate(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

async function requireAuthenticatedProfileId() {
  const client = getSupabaseClient();
  if (!client) throw new Error('Supabase belum dikonfigurasi.');
  const session = await client.auth.getSession();
  if (session.error) throw session.error;
  const profileId = session.data.session?.user.id;
  if (!profileId) throw new Error('Sesi pengguna belum tersedia.');
  return { client, profileId };
}

export async function listTutorLearnerScopes(onDate = localIsoDate()): Promise<TutorLearnerScopeOption[]> {
  const { client, profileId } = await requireAuthenticatedProfileId();
  const teacher = await client
    .from('teachers')
    .select('id')
    .eq('profile_id', profileId)
    .eq('status', 'active')
    .single();
  if (teacher.error) throw teacher.error;

  const links = await client
    .from('teacher_students')
    .select('student_id,subject_id,status,active_from,active_until')
    .eq('teacher_id', teacher.data.id)
    .eq('status', 'active');
  if (links.error) throw links.error;

  const active = (links.data ?? []).map((row) => ({
    studentId: String(row.student_id),
    subjectId: String(row.subject_id),
    status: String(row.status),
    activeFrom: String(row.active_from),
    activeUntil: row.active_until ? String(row.active_until) : null,
  })).filter((scope) => isTutorAssignmentActive(scope, onDate));

  if (!active.length) return [];

  const studentIds = [...new Set(active.map((row) => row.studentId))];
  const subjectIds = [...new Set(active.map((row) => row.subjectId))];
  const [students, subjects] = await Promise.all([
    client.from('students').select('id,student_code,display_name').in('id', studentIds),
    client.from('subjects').select('id,code,name').in('id', subjectIds),
  ]);
  if (students.error) throw students.error;
  if (subjects.error) throw subjects.error;

  const studentMap = new Map((students.data ?? []).map((row) => [String(row.id), row as StudentRow]));
  const subjectMap = new Map((subjects.data ?? []).map((row) => [String(row.id), row as SubjectRow]));

  return active.flatMap((scope) => {
    const student = studentMap.get(scope.studentId);
    const subject = subjectMap.get(scope.subjectId);
    if (!student || !subject) return [];
    return [{
      ...scope,
      studentCode: student.student_code,
      studentName: student.display_name,
      subjectCode: subject.code,
      subjectName: subject.name,
    }];
  }).sort((a, b) => a.studentName.localeCompare(b.studentName, 'id') || a.subjectName.localeCompare(b.subjectName, 'id'));
}

const TUTOR_ROSTER_PROGRESS_PAGE_SIZE = 1000;

async function fetchTutorRosterProgress(
  client: NonNullable<ReturnType<typeof getSupabaseClient>>,
  studentIds: string[],
): Promise<RosterProgressRow[]> {
  const rows: RosterProgressRow[] = [];
  for (let from = 0; ; from += TUTOR_ROSTER_PROGRESS_PAGE_SIZE) {
    const result = await client
      .from('student_skill_progress')
      .select('student_id,skill_id,mastery_state,last_activity_at')
      .in('student_id', studentIds)
      .order('student_id', { ascending: true })
      .order('skill_id', { ascending: true })
      .range(from, from + TUTOR_ROSTER_PROGRESS_PAGE_SIZE - 1);
    if (result.error) throw result.error;
    const page = (result.data ?? []) as RosterProgressRow[];
    rows.push(...page);
    if (page.length < TUTOR_ROSTER_PROGRESS_PAGE_SIZE) break;
  }
  return rows;
}

export async function loadTutorRoster(onDate = localIsoDate()): Promise<TutorRosterBundle> {
  const scopes = await listTutorLearnerScopes(onDate);
  if (!scopes.length) return { scopes: [], students: [] };

  const { client } = await requireAuthenticatedProfileId();
  const studentIds = [...new Set(scopes.map((scope) => scope.studentId))];
  const subjectIds = [...new Set(scopes.map((scope) => scope.subjectId))];

  const skillsResult = await client
    .from('skills')
    .select('id,subject_id')
    .in('subject_id', subjectIds)
    .eq('status', 'active');
  if (skillsResult.error) throw skillsResult.error;

  const skillRows = (skillsResult.data ?? []) as RosterSkillRow[];
  const skillMap = new Map(skillRows.map((row) => [String(row.id), String(row.subject_id)]));

  const progressRows = await fetchTutorRosterProgress(client, studentIds);

  const allowedScopeKeys = new Set(scopes.map((scope) => `${scope.studentId}:${scope.subjectId}`));
  const studentMap = new Map<string, TutorRosterStudent>();

  for (const scope of scopes) {
    let student = studentMap.get(scope.studentId);
    if (!student) {
      student = {
        studentId: scope.studentId,
        studentCode: scope.studentCode,
        studentName: scope.studentName,
        subjects: [],
        attentionCount: 0,
        learningCount: 0,
        strongCount: 0,
        lastActivityAt: null,
      };
      studentMap.set(scope.studentId, student);
    }
    student.subjects.push({
      subjectId: scope.subjectId,
      subjectCode: scope.subjectCode,
      subjectName: scope.subjectName,
      attentionCount: 0,
      learningCount: 0,
      strongCount: 0,
      lastActivityAt: null,
    });
  }

  for (const row of progressRows) {
    const studentId = String(row.student_id);
    const subjectId = skillMap.get(String(row.skill_id));
    if (!subjectId || !allowedScopeKeys.has(`${studentId}:${subjectId}`)) continue;

    const student = studentMap.get(studentId);
    const subject = student?.subjects.find((item) => item.subjectId === subjectId);
    if (!student || !subject) continue;

    const category = classifyMasteryState(row.mastery_state);
    if (category === 'attention') {
      student.attentionCount += 1;
      subject.attentionCount += 1;
    } else if (category === 'learning') {
      student.learningCount += 1;
      subject.learningCount += 1;
    } else if (category === 'strong') {
      student.strongCount += 1;
      subject.strongCount += 1;
    }

    const lastActivityAt = row.last_activity_at ? String(row.last_activity_at) : null;
    if (lastActivityAt && (!student.lastActivityAt || lastActivityAt > student.lastActivityAt)) {
      student.lastActivityAt = lastActivityAt;
    }
    if (lastActivityAt && (!subject.lastActivityAt || lastActivityAt > subject.lastActivityAt)) {
      subject.lastActivityAt = lastActivityAt;
    }
  }

  return {
    scopes,
    students: [...studentMap.values()]
      .map((student) => ({
        ...student,
        subjects: [...student.subjects].sort((a, b) => a.subjectName.localeCompare(b.subjectName, 'id')),
      }))
      .sort((a, b) => a.studentName.localeCompare(b.studentName, 'id')),
  };
}

export async function listParentLearnerScopes(): Promise<ParentLearnerScopeOption[]> {
  const { client, profileId } = await requireAuthenticatedProfileId();
  const guardian = await client
    .from('guardians')
    .select('id')
    .eq('profile_id', profileId)
    .eq('status', 'active')
    .single();
  if (guardian.error) throw guardian.error;

  const links = await client
    .from('student_guardians')
    .select('student_id,relationship_label,is_primary,can_view_progress')
    .eq('guardian_id', guardian.data.id);
  if (links.error) throw links.error;

  if (!links.data?.length) return [];
  const studentIds = [...new Set(links.data.map((row) => String(row.student_id)))];
  const students = await client
    .from('students')
    .select('id,student_code,display_name')
    .in('id', studentIds);
  if (students.error) throw students.error;
  const studentMap = new Map((students.data ?? []).map((row) => [String(row.id), row as StudentRow]));

  return links.data.flatMap((row) => {
    const studentId = String(row.student_id);
    const student = studentMap.get(studentId);
    if (!student) return [];
    return [{
      studentId,
      canViewProgress: Boolean(row.can_view_progress),
      studentCode: student.student_code,
      studentName: student.display_name,
      relationshipLabel: String(row.relationship_label),
      isPrimary: Boolean(row.is_primary),
    }];
  }).sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.studentName.localeCompare(b.studentName, 'id'));
}

export async function listParentLearnerSubjects(studentId: string): Promise<ParentLearnerSubjectOption[]> {
  const scopes = await listParentLearnerScopes();
  assertParentProgressScope(scopes, studentId);

  const { client } = await requireAuthenticatedProfileId();
  const progressResult = await client
    .from('student_subject_progress')
    .select('subject_id,updated_at')
    .eq('student_id', studentId)
    .order('updated_at', { ascending: false });
  if (progressResult.error) throw progressResult.error;

  const subjectIds = [...new Set(
    (progressResult.data ?? [])
      .map((row) => row.subject_id ? String(row.subject_id) : '')
      .filter(Boolean),
  )];

  if (!subjectIds.length) return [];

  const subjectsResult = await client
    .from('subjects')
    .select('id,code,name')
    .in('id', subjectIds);
  if (subjectsResult.error) throw subjectsResult.error;

  const subjectMap = new Map(
    (subjectsResult.data ?? []).map((row) => [String(row.id), row as SubjectRow]),
  );
  const activityMap = new Map<string, string | null>();
  for (const row of progressResult.data ?? []) {
    const subjectId = row.subject_id ? String(row.subject_id) : '';
    if (!subjectId || activityMap.has(subjectId)) continue;
    activityMap.set(subjectId, row.updated_at ? String(row.updated_at) : null);
  }

  return subjectIds.flatMap((subjectId) => {
    const subject = subjectMap.get(subjectId);
    if (!subject) return [];
    return [{
      subjectId,
      subjectCode: subject.code,
      subjectName: subject.name,
      lastActivityAt: activityMap.get(subjectId) ?? null,
    }];
  }).sort((a, b) => a.subjectName.localeCompare(b.subjectName, 'id'));
}


async function loadSubjectInsightInput(input: {
  studentId: string;
  subjectId: string;
  noteVisibilities: LearnerNoteVisibility[];
}): Promise<LearnerInsightInput> {
  const { client } = await requireAuthenticatedProfileId();
  const { studentId, subjectId, noteVisibilities } = input;

  const [studentResult, subjectResult, subjectProgressResult, skillResult] = await Promise.all([
    client.from('students').select('id,student_code,display_name').eq('id', studentId).single(),
    client.from('subjects').select('id,code,name').eq('id', subjectId).single(),
    client.from('student_subject_progress').select('current_level_id,updated_at').eq('student_id', studentId).eq('subject_id', subjectId).maybeSingle(),
    client.from('skills').select('id,code,learner_statement,canonical_statement').eq('subject_id', subjectId).eq('status', 'active'),
  ]);

  if (studentResult.error) throw studentResult.error;
  if (subjectResult.error) throw subjectResult.error;
  if (subjectProgressResult.error) throw subjectProgressResult.error;
  if (skillResult.error) throw skillResult.error;

  const student = studentResult.data as StudentRow;
  const subject = subjectResult.data as SubjectRow;
  const skills = (skillResult.data ?? []) as SkillRow[];
  const skillIds = skills.map((row) => row.id);

  const levelId = subjectProgressResult.data?.current_level_id ? String(subjectProgressResult.data.current_level_id) : null;
  const levelResult = levelId
    ? await client.from('levels').select('id,code,display_name').eq('id', levelId).eq('subject_id', subjectId).single()
    : { data: null, error: null };
  if (levelResult.error) throw levelResult.error;

  const progressResult = skillIds.length
    ? await client
      .from('student_skill_progress')
      .select('skill_id,mastery_state,mastery_percent,evidence_count,last_activity_at,average_active_seconds,average_hint_count')
      .eq('student_id', studentId)
      .in('skill_id', skillIds)
    : { data: [], error: null };
  if (progressResult.error) throw progressResult.error;

  const skillMap = new Map(skills.map((row) => [row.id, row]));
  const progressRows = (progressResult.data ?? []) as SkillProgressRow[];
  const skillProgress: LearnerSkillProgressInput[] = progressRows.flatMap((row) => {
    const skill = skillMap.get(String(row.skill_id));
    if (!skill) return [];
    return [{
      skillId: skill.id,
      code: skill.code,
      learnerStatement: skill.learner_statement || skill.canonical_statement,
      masteryState: row.mastery_state,
      masteryPercent: Number(row.mastery_percent),
      evidenceCount: Number(row.evidence_count),
      lastActivityAt: row.last_activity_at ? String(row.last_activity_at) : null,
      averageActiveSeconds: row.average_active_seconds == null ? null : Number(row.average_active_seconds),
      averageHintCount: row.average_hint_count == null ? null : Number(row.average_hint_count),
    }];
  });

  const assignmentsResult = await client
    .from('assignments')
    .select('id,assigned_date,assignment_type,status,updated_at')
    .eq('student_id', studentId)
    .eq('subject_id', subjectId)
    .order('assigned_date', { ascending: false })
    .limit(40);
  if (assignmentsResult.error) throw assignmentsResult.error;
  const assignments = (assignmentsResult.data ?? []) as HistoryAssignmentRow[];
  const assignmentIds = assignments.map((row) => row.id);

  const sessionsResult = await client
    .from('learning_sessions')
    .select('id,assignment_id,started_at,ended_at,active_duration_ms,status')
    .eq('student_id', studentId)
    .eq('subject_id', subjectId)
    .order('started_at', { ascending: false })
    .limit(30);
  if (sessionsResult.error) throw sessionsResult.error;
  const sessions = (sessionsResult.data ?? []) as HistorySessionRow[];

  const assignmentItemsResult = assignmentIds.length
    ? await client
      .from('assignment_items')
      .select('id,assignment_id,sequence')
      .in('assignment_id', assignmentIds)
    : { data: [], error: null };
  if (assignmentItemsResult.error) throw assignmentItemsResult.error;
  const assignmentItems = (assignmentItemsResult.data ?? []) as HistoryAssignmentItemRow[];
  const assignmentItemIds = assignmentItems.map((row) => row.id);

  const attemptsResult = assignmentItemIds.length
    ? await client
      .from('attempts')
      .select('id,assignment_item_id,learning_session_id,status,started_at,submitted_at,active_duration_ms')
      .eq('student_id', studentId)
      .in('assignment_item_id', assignmentItemIds)
    : { data: [], error: null };
  if (attemptsResult.error) throw attemptsResult.error;
  const attempts = (attemptsResult.data ?? []) as HistoryAttemptRow[];
  const attemptIds = attempts.map((row) => row.id);

  const answersResult = attemptIds.length
    ? await client
      .from('attempt_answers')
      .select('id,attempt_id,first_attempt_correct,final_correct,hint_count,active_duration_ms,answered_at')
      .in('attempt_id', attemptIds)
    : { data: [], error: null };
  if (answersResult.error) throw answersResult.error;
  const answers = (answersResult.data ?? []) as HistoryAnswerRow[];

  let noteQuery = client
    .from('student_notes')
    .select('id,visibility,body,created_at,subject_id,skill_id,assignment_id')
    .eq('student_id', studentId)
    .in('visibility', noteVisibilities)
    .or(`subject_id.is.null,subject_id.eq.${subjectId}`)
    .order('created_at', { ascending: false })
    .limit(30);
  const notesResult = await noteQuery;
  if (notesResult.error) throw notesResult.error;

  const notes: LearnerNoteInsight[] = (notesResult.data ?? []).map((row) => ({
    id: String(row.id),
    visibility: row.visibility as LearnerNoteVisibility,
    body: String(row.body),
    createdAt: String(row.created_at),
    ...(row.subject_id ? { subjectId: String(row.subject_id) } : {}),
    ...(row.skill_id ? { skillId: String(row.skill_id) } : {}),
    ...(row.assignment_id ? { assignmentId: String(row.assignment_id) } : {}),
  }));

  const recentSessions = buildLearningSessionSummaries({
    assignments,
    assignmentItems,
    sessions,
    attempts,
    answers,
  });

  return {
    learner: {
      studentId: student.id,
      studentCode: student.student_code,
      displayName: student.display_name,
    },
    subject: {
      subjectId: subject.id,
      code: subject.code,
      name: subject.name,
    },
    currentLevel: levelResult.data ? {
      levelId: String(levelResult.data.id),
      code: String(levelResult.data.code),
      displayName: String(levelResult.data.display_name),
    } : null,
    skills: skillProgress,
    recentSessions,
    notes,
    updatedAt: new Date().toISOString(),
  };
}

export async function loadTutorLearnerInsight(
  studentId: string,
  subjectId: string,
  onDate = localIsoDate(),
): Promise<LearnerInsight> {
  const scopes = await listTutorLearnerScopes(onDate);
  assertTutorSubjectScope(scopes, studentId, subjectId, onDate);
  return buildLearnerInsight(await loadSubjectInsightInput({
    studentId,
    subjectId,
    noteVisibilities: ['staff_only', 'guardian_shared'],
  }));
}

export async function loadParentLearnerInsight(studentId: string, subjectId: string): Promise<LearnerInsight> {
  const scopes = await listParentLearnerScopes();
  assertParentProgressScope(scopes, studentId);
  return buildLearnerInsight(await loadSubjectInsightInput({
    studentId,
    subjectId,
    noteVisibilities: ['guardian_shared'],
  }));
}
